import { parseBoolean, type SchemaField } from "@/lib/tableBlockHelpers";

export const defaultColumnMapping: Record<number, string> = {
  0: "procedureDate",
  1: "examCode",
  2: "procedureName",
  3: "mrn",
  4: "accessionNumber",
};

export type ImportSchemaField = SchemaField & {
  importAliases?: string[];
};

export type ImportResultSource = "textract" | "csv";

export type TextractResult = {
  fileName: string;
  textractResponse: string[][];
  columnMapping: Record<number, string>;
  originalHeaders?: string[];
  source?: ImportResultSource;
};

export function isCsvFile(file: File): boolean {
  const name = file.name.toLowerCase();
  if (name.endsWith(".csv")) return true;
  const type = file.type.toLowerCase();
  return type === "text/csv" || type === "application/csv";
}

export function isImageFile(file: File): boolean {
  return file.type.startsWith("image/");
}

/** RFC 4180–style CSV parse (quoted fields, escaped quotes, CRLF/LF). */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    const next = text[i + 1];

    if (inQuotes) {
      if (char === '"' && next === '"') {
        cell += '"';
        i++;
      } else if (char === '"') {
        inQuotes = false;
      } else {
        cell += char;
      }
      continue;
    }

    if (char === '"') {
      inQuotes = true;
    } else if (char === ",") {
      row.push(cell);
      cell = "";
    } else if (char === "\n" || (char === "\r" && next !== "\n")) {
      row.push(cell);
      rows.push(row);
      row = [];
      cell = "";
    } else if (char !== "\r") {
      cell += char;
    }
  }

  if (cell.length > 0 || row.length > 0) {
    row.push(cell);
    rows.push(row);
  }

  return rows.filter(
    (r) => r.length > 1 || r.some((c) => c.trim() !== ""),
  );
}

export function buildImportResultFromTableData({
  fileName,
  tableData,
  schemaHeaders,
  schema = [],
  source,
}: {
  fileName: string;
  tableData: string[][];
  schemaHeaders: Record<string, string>;
  schema?: ImportSchemaField[];
  source: ImportResultSource;
}): TextractResult {
  const firstRow = tableData[0] ?? [];
  const { mapping, useDefault } = detectHeaderMappingFromFirstRow({
    firstRow,
    schemaHeaders,
    schema,
  });
  return {
    fileName,
    textractResponse: useDefault ? tableData : tableData.slice(1),
    columnMapping: mapping,
    originalHeaders: firstRow,
    source,
  };
}

type ProcessedBlock = Record<string, unknown>;

export function buildHeadersFromSchema(
  schema: Array<{ key: string; name: string }>,
): Record<string, string> {
  return schema.reduce(
    (acc, column) => {
      acc[column.key] = column.name;
      return acc;
    },
    {} as Record<string, string>,
  );
}

export function detectHeaderMappingFromFirstRow({
  firstRow,
  schemaHeaders,
  schema = [],
}: {
  firstRow: string[];
  schemaHeaders: Record<string, string>;
  schema?: ImportSchemaField[];
}): { mapping: Record<number, string>; useDefault: boolean } {
  const mapping: Record<number, string> = {};
  const aliasMap: Record<string, string> = {};

  for (const column of schema) {
    if (column.importAliases && Array.isArray(column.importAliases)) {
      for (const alias of column.importAliases) {
        aliasMap[alias.toLowerCase().trim()] = column.key;
      }
    }
  }

  firstRow.forEach((cellValue, colIndex) => {
    const normalizedCell = cellValue.toLowerCase().trim();

    if (aliasMap[normalizedCell]) {
      mapping[colIndex] = aliasMap[normalizedCell];
      return;
    }

    const matchedKey = Object.entries(schemaHeaders).find(
      ([, displayName]) => displayName.toLowerCase() === normalizedCell,
    );
    if (matchedKey) {
      mapping[colIndex] = matchedKey[0];
      return;
    }

    const matchedKeyDirect = Object.keys(schemaHeaders).find(
      (key) => key.toLowerCase() === normalizedCell,
    );
    if (matchedKeyDirect) {
      mapping[colIndex] = matchedKeyDirect;
    }
  });

  if (Object.keys(mapping).length === 0) {
    return { mapping: defaultColumnMapping, useDefault: true };
  }

  return { mapping, useDefault: false };
}

export function processRow({
  row,
  columnMapping,
  schema = [],
}: {
  row: string[];
  columnMapping: Record<number, string>;
  schema?: ImportSchemaField[];
}): ProcessedBlock {
  const block: ProcessedBlock = {};
  const fieldTypes = schema.reduce(
    (acc, col) => {
      acc[col.key] = col.type;
      return acc;
    },
    {} as Record<string, string>,
  );

  row.forEach((value, colIndex) => {
    const mappedField = columnMapping[colIndex];
    if (!mappedField) return;
    const fieldType = fieldTypes[mappedField];
    if (fieldType === "date") {
      const trimmedValue = value?.trim();
      if (trimmedValue) {
        block[mappedField] = new Date(value);
      }
    } else {
      block[mappedField] = value;
    }
  });

  for (const col of schema) {
    if (!(col.key in block)) {
      if (col.type === "date") continue;
      block[col.key] = "";
    }
  }

  return block;
}

export function formatImportedRowData({
  block,
  schema = [],
}: {
  block: ProcessedBlock;
  schema?: ImportSchemaField[];
}): Record<string, { value?: unknown }> {
  const data: Record<string, { value?: unknown }> = {};
  const fieldTypes = schema.reduce(
    (acc, col) => {
      acc[col.key] = col.type;
      return acc;
    },
    {} as Record<string, string>,
  );

  for (const [key, value] of Object.entries(block)) {
    const fieldType = fieldTypes[key];
    if (fieldType === "date") {
      if (value instanceof Date) {
        data[key] = { value: value.toISOString().split("T")[0] };
      } else if (value) {
        data[key] = { value: String(value).slice(0, 10) };
      }
    } else if (fieldType === "number") {
      const num = typeof value === "number" ? value : Number(value);
      data[key] = { value: Number.isNaN(num) ? undefined : num };
    } else if (fieldType === "checkbox") {
      data[key] = { value: parseBoolean(value) };
    } else {
      data[key] = { value };
    }
  }

  for (const col of schema) {
    if (col.key in data) continue;
    if (col.type === "date") continue;
    data[col.key] = { value: col.type === "checkbox" ? undefined : "" };
  }

  return data;
}

export function generateObjectId(): string {
  return (
    Math.floor(Date.now() / 1000).toString(16) +
    "xxxxxxxxxxxxxxxx".replace(/[x]/g, () =>
      (Math.random() * 16).toString(16)[0],
    )
  );
}
